import { useEffect, useMemo } from 'react'

import { invalidateGroupCommunitiesEventListener } from '@space-lib-events/invalidateGroupCommunitiesQuery'
import { setProductFruitsUserEventDispatch } from '@space-lib-events/productFruitsUser'
import Loader from '@space-lib/components/loader'

import type { CommunityProps } from '@space-shell/common/services/communities'
import { UserInfoRole } from '@space-shell/common/services/user'
import { UrlUtils } from '@space-shell/common/utils/url'
import { RoutesPaths } from '@space-shell/core/router'
import { useUserInfoStore } from '@space-shell/core/store/useUserInfoStore'

import type { CommunitySlugMap } from '@space-shell/modules/communities/context/communitiesSlugMapContext/types'
import { useGetGroups } from '@space-shell/modules/communities/hooks'
import useCommunitiesSlugMap from '@space-shell/modules/communities/hooks/useCommunitiesSlugMap'

import useMenuCollapse from '@space-shell/modules/communities/hooks/useMenuCollapse'
import { useLocation } from 'react-router-dom'

import useMenuCommunity from './hooks/useMenuCommunity'
import { useSparkleCreateProfile } from './hooks/useSparkleCreateProfile'
import { MenuCollapse } from './menuCollapse'

const MenuCommunityItem = () => {
  const { isLoadingSparkleCreateProfile } = useSparkleCreateProfile()
  const location = useLocation()
  const { setGroupCommunitiesLength, setCommunitiesLength } = useMenuCollapse()
  const user = useUserInfoStore()
  const { setCommunitiesSlugMap } = useCommunitiesSlugMap()
  const { handleCommunityClick } = useMenuCommunity()
  const {
    groupCommunitiesList,
    isLoadingGetGroupCommunities,
    isRefetchingGetGroupCommunities,
    invalidateGroupCommunitiesQuery
  } = useGetGroups()

  const communities = useMemo(
    () =>
      groupCommunitiesList?.reduce((communitiesList, item) => {
        if (!item?.communities?.data?.length) return communitiesList

        return [...communitiesList, ...item?.communities?.data]
      }, [] as CommunityProps[]) ?? [],
    [groupCommunitiesList]
  )

  const isOwnerUser = useMemo(() => user?.role === UserInfoRole.OWNER, [user])

  useEffect(() => {
    const groupsLen = Number(groupCommunitiesList?.length) || 0
    const communitiesLen = Number(communities.length) || 0

    setCommunitiesLength(communitiesLen)
    setGroupCommunitiesLength(groupsLen)

    setProductFruitsUserEventDispatch({
      amountOfCommunities: communitiesLen,
      hasGroups: groupsLen > 0
    })
  }, [groupCommunitiesList, communities, setCommunitiesLength, setGroupCommunitiesLength])

  useEffect(() => {
    invalidateGroupCommunitiesEventListener(() => {
      invalidateGroupCommunitiesQuery()
    })
  }, [invalidateGroupCommunitiesQuery])

  useEffect(() => {
    setCommunitiesSlugMap(
      communities.map((community) => ({
        id: community.id,
        slug: community.url
      })) as CommunitySlugMap[]
    )
  }, [communities, setCommunitiesSlugMap])

  if (!isOwnerUser && communities.length < 1) {
    return null
  }

  const isActive = (idCommunity: number) =>
    UrlUtils.isMenuItemActive(
      `${RoutesPaths.HOME}${RoutesPaths.COMMUNITY}/${idCommunity}`,
      location.pathname,
      false
    )

  if (
    isLoadingGetGroupCommunities ||
    isRefetchingGetGroupCommunities ||
    !groupCommunitiesList ||
    isLoadingSparkleCreateProfile
  ) {
    return (
      <div slot='items'>
        <Loader full />
      </div>
    )
  }

  return (
    <>
      {groupCommunitiesList.map(({ communities, ...group }) => (
        <MenuCollapse
          key={group.id}
          group={group}
          redirectCommunity={handleCommunityClick}
          communities={communities.data ?? []}
          isActive={isActive}
        />
      ))}
    </>
  )
}

export default MenuCommunityItem
